import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { bookingAllows } from "../../utils/helpers/standardScope";
import { visitFocusStandards } from "./FocusStandardChips";

/** The picks to send for a booking in `subject`: those the picker shows. The
 *  server stores the focus as sent (checked only against the rubric), so a
 *  pick a lesson change hid must not ride along unseen. */
export const shownFocus = (rubric, value, subject) =>
  visitFocusStandards(rubric, value, subject).map((std) => std.uuid);

// Multi-select twin of FocusStandardChips: togglable focus-standard pills for
// the scheduling modals. Owns the rubric-order sorting and the toggle logic so
// callers only hold the selected-uuids state.
// `subject` (the booked lesson's) narrows the pills to the standards that
// lesson is scored on; unknown offers them all. Callers send shownFocus; a
// reschedule keeps the stored list, and screens filter it at read time
// (visitFocusStandards).
export default function FocusStandardPicker({ rubric, subject = null, value, onChange, className = "" }) {
  const { t } = useTranslation();
  const standards = useMemo(
    () =>
      (rubric?.standards || [])
        .filter((std) => bookingAllows(std, subject))
        .sort((a, b) => a.order - b.order),
    [rubric, subject],
  );

  const toggle = (uuid) =>
    onChange(value.includes(uuid) ? value.filter((u) => u !== uuid) : [...value, uuid]);

  if (standards.length === 0) return null;

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {standards.map((std) => {
        const active = value.includes(std.uuid);
        return (
          <button
            key={std.uuid}
            type="button"
            onClick={() => toggle(std.uuid)}
            title={std.title}
            className={`inline-flex items-center gap-1.5 max-w-full px-3 py-1.5 rounded-full text-xs font-bold border transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
              active
                ? "bg-indigo-600 border-indigo-600 text-white"
                : "bg-white border-slate-200 text-slate-600 hover:border-indigo-300"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${active ? "bg-white" : "bg-indigo-500"}`}
              aria-hidden="true"
            />
            <span className="truncate">
              {t("upcoming.standardChip", {
                number: std.number || std.order,
                title: std.title,
              })}
            </span>
          </button>
        );
      })}
    </div>
  );
}
